import api from '@shared/services/api';
import { t } from 'i18next';
export const personService = {
    createPerson: async (personData) => {
        try {
            const response = await api.post('/persons', personData);
            return response.data;
        } catch (error) {
            console.error('Error creating person:', error);
            throw error;
        }
    },
    updatePerson: async (personId, formData) => {
        try {
            const response = await api.put(`/persons/${personId}`, formData);
            return response.data;
        } catch (error) {
            console.error('Error updating person:', error);
            throw error;
        }
    },
    updatePersonAddData: async (personId, formData) => {
        try {
            const response = await api.put(`/persons/add-data/${personId}`, formData);
            return response.data;
        } catch (error) {
            console.error('Error updating person additional data:', error);
            throw error;
        }
    },
    deletePerson: async (personId) => {
        try {
            const response = await api.delete(`/persons/${personId}`);
            return response.data;
        } catch (error) {
            console.error('Error deleting person:', error);
            throw error;
        }
    },

    deactivatePerson: async (personId) => {
        try {
            const response = await api.put(`/persons/deactivate/${personId}`);
            return response.data;
        } catch (error) {
            console.error('Error deactivating person:', error);
            throw error;
        }
    },

    activatePerson: async (personId) => {
        try {
            const response = await api.put(`/persons/activate/${personId}`);
            return response.data;
        } catch (error) {
            console.error('Error activating person:', error);
            throw error;
        }
    },

    getEmpBranch: async (params = {}) => {
        const queryParams = new URLSearchParams();
        if (params.idn?.trim()) queryParams.append('idn', params.idn.trim());
        if (params.name?.trim()) queryParams.append('name', params.name.trim());
        const queryString = queryParams.toString();
        const url = `/persons/emp-branch${queryString ? `?${queryString}` : ''}`;
        const response = await api.get(url);
        return response.data;
    },

    getPersons: async ({
        searchQuery,
        searchType,
        pageNumber = 0,
        pageSize = 10,
        sortBy = 'name',
        sortDir = 'asc',
        active,
        ...searchParams
    }) => {
        try {
            const params = {
                page: pageNumber,
                size: pageSize,
                sortBy: sortBy || 'name',
                sortOrder: sortDir,
            };

            if (active !== undefined && active !== null && active !== '') {
                params.active = active === true || active === 'true';
            }

            // Handle multiple search parameters
            if (searchQuery && typeof searchQuery === 'object') {
                // Add each search parameter directly to params
                Object.entries(searchQuery).forEach(([key, value]) => {
                    if (value && value.trim()) {
                        params[key] = value.trim();
                    }
                });
            } else if (searchQuery && typeof searchQuery === 'string') {
                // Handle legacy string search query
                if (searchType) {
                    params[searchType] = searchQuery.trim();
                }
            }

            // Add any additional search parameters
            Object.entries(searchParams).forEach(([key, value]) => {
                if (value === undefined || value === null || value === '') {
                    return;
                }
                if (typeof value === 'string' && value.trim()) {
                    params[key] = value.trim();
                }
            });

            const response = await api.get('/persons', { params });

            const data = response?.data || {};
            const embeddedList = data?._embedded?.personsDTOList
                || data?._embedded?.personsResponseDTOList;
            const persons = Array.isArray(data?.content)
                ? data.content
                : Array.isArray(embeddedList) ? embeddedList : [];

            if (!Array.isArray(persons) || persons.length === 0) {
                if (!Array.isArray(embeddedList) && !Array.isArray(data?.content)) {
                    console.error('Invalid response - no persons data:', data);
                    throw new Error(t('common.dataNotFound'));
                }
            }

            const totalElements = data?.totalElements
                ?? data?.page?.totalElements
                ?? persons.length
                ?? 0;

            const totalPages = data?.totalPages
                ?? data?.page?.totalPages
                ?? Math.ceil(totalElements / (data?.pageSize || pageSize))
                ?? 0;

            const currentPageNumber = data?.pageNumber
                ?? data?.page?.number
                ?? pageNumber;

            return {
                content: persons,
                totalPages,
                totalElements,
                pageNumber: currentPageNumber
            };
        } catch (error) {
            console.error('Error fetching/searching persons:', error);
            throw error;
        }
    },
    getExtendedAccessPersons: async ({
        searchQuery,
        searchType,
        pageNumber = 0,
        pageSize = 10,
        sortBy = 'username',
        sortDir = 'asc',
        ...searchParams
    }) => {
        try {
            const params = {
                page: pageNumber,
                size: pageSize,
                sortBy: sortBy || 'username',
                sortOrder: sortDir,
            };

            // Handle multiple search parameters
            if (searchQuery && typeof searchQuery === 'object') {
                // Add each search parameter directly to params
                Object.entries(searchQuery).forEach(([key, value]) => {
                    if (value && value.trim()) {
                        params[key] = value.trim();
                    }
                });
            } else if (searchQuery && typeof searchQuery === 'string') {
                // Handle legacy string search query
                if (searchType) {
                    params[searchType] = searchQuery.trim();
                }
            }

            // Add any additional search parameters
            Object.entries(searchParams).forEach(([key, value]) => {
                if (value && value.trim()) {
                    params[key] = value.trim();
                }
            });

            const response = await api.get('/users/extended-access', { params });
            return {
                content: response.data.content || response.data || [],
                totalPages: response.data.totalPages || 0,
                totalElements: response.data.totalElements || 0,
                pageNumber: response.data.pageable?.pageNumber || pageNumber
            };
        } catch (error) {
            console.error('Error fetching extended access persons:', error);
            throw error;
        }
    },


    getPersonById: async (personId) => {
        try {
            const response = await api.get(`/persons/${personId}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching person:', error);
            throw error;
        }
    },
    getPersonAddData: async (personId) => {
        try {
            const response = await api.get(`/persons/add-data/${personId}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching person add data:', error);
            throw error;
        }
    },
    getPersonFullName: async () => {
        try {
            const response = await api.get('/users/person-full-name');
            return response.data;
        } catch (error) {
            console.error('Error fetching person full name:', error);
            throw error;
        }
    },
    getPersonEmails: async (personId) => {
        try {
            const response = await api.get(`/personal-emails/${personId}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching person emails:', error);
            throw error;
        }
    },
    addPersonEmail: async ( emailData) => {
        try {
            const payload = {
                email: emailData.email,
                emailType: emailData.emailType,
                personId: emailData.personId
            };
            const response = await api.post('/personal-emails', payload);
            return response.data;
        } catch (error) {
            console.error('Error adding person email:', error);
            throw error;
        }
    },
    updatePersonEmail: async ( emailData, emailId) => {
        try {
            const payload = {
                email: emailData.email,
                emailType: emailData.emailType,
                personId: emailData.personId
            };
            const response = await api.put(`/personal-emails/${emailId}`, payload);
            return response.data;
        } catch (error) {
            console.error('Error updating person email:', error);
            throw error;
        }
    },
    deletePersonEmail: async (emailId) => {
        try {
            const response = await api.delete(`/personal-emails/${emailId}`);
            return response.data;
        } catch (error) {
            console.error('Error deleting person email:', error);
            throw error;
        }
    },
    getBanks: async ({ page = 0, size = 10, sortBy = 'name', sortDir = 'asc' }) => {
        try {
            const params = {
                page,
                size,
                sortBy: sortBy,
                sortOrder: sortDir
            };

            const response = await api.get('/banks', { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching banks:', error);
            throw error;
        }
    },
    banksSelect: async () => {
        try {
            const response = await api.get('/banks/list');
            return response.data;
        } catch (error) {
            console.error('Error fetching banks:', error);
            throw error;
        }
    },
    addBank: async (bankData) => {
        try {
            const response = await api.post('/banks', bankData);
            return response.data;
        } catch (error) {
            console.error('Error adding bank:', error);
            throw error;
        }
    },
    updateBank: async (bankId, bankData) => {
        try {
            const response = await api.put(`/banks/${bankId}`, bankData);
            return response.data;
        } catch (error) {
            console.error('Error updating bank:', error);
            throw error;
        }
    },
    deleteBank: async (bankId) => {
        try {
            const response = await api.delete(`/banks/${bankId}`);
            return response.data;
        } catch (error) {
            console.error('Error deleting bank:', error);
            throw error;
        }
    },

    getPersonPayments: async (personId) => {
        try {
            const response = await api.get(`/personal-bank-accounts/${personId}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching person bank accounts:', error);
            throw error;
        }
    },
    addPersonPayment: async (personId, data) => {
        try {
            const payload = {
                personId: personId,
                bankId: data.bankId,
                iban: data.iban,
                description: data.description,
                isActive: data.isActive
            };
            const response = await api.post('/personal-bank-accounts', payload);
            return response.data;
        } catch (error) {
            console.error('Error adding person bank account:', error);
            throw error;
        }
    },
    updatePersonPayment: async (personId, paymentId, data) => {
        try {
            const payload = {
                personId: personId,
                bankId: data.bankId,
                iban: data.iban,
                description: data.description,
                isActive: data.isActive
            };

            const response = await api.put(`/personal-bank-accounts/${paymentId}`, payload);
            return response.data;
        } catch (error) {
            console.error('Error updating person bank account:', error);
            throw error;
        }
    },
    deletePersonPayment: async (paymentId) => {
        try {
            const response = await api.delete(`/personal-bank-accounts/${paymentId}`);
            return response.data;
        } catch (error) {
            console.error('Error deleting person bank account:', error);
            throw error;
        }
    },
    getPhysicians: async (personId) => {
        try {

            const response = await api.get(`/emp-jp-doctors/${personId}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching physicians:', error);
            throw error;
        }
    },
    addPhysician: async (personId, data) => {
        try {
            const payload = {
                personId: personId,
                effectiveDate: data.effectiveDate,
                jpName: data.jpName,
                jpAddress: data.jpAddress,
                jpPhone: data.jpPhone
            };
            const response = await api.post('/emp-jp-doctors', payload);
            return response.data;
        } catch (error) {
            console.error('Error adding physician:', error);
            throw error;
        }
    },
    updatePhysician: async (personId, physicianId, data) => {
        try {
            const payload = {
                personId: personId,
                effectiveDate: data.effectiveDate,
                jpName: data.jpName,
                jpAddress: data.jpAddress,
                jpPhone: data.jpPhone
            };
            const response = await api.put(`/emp-jp-doctors/${physicianId}`, payload);
            return response.data;
        } catch (error) {
            console.error('Error updating physician:', error);
            throw error;
        }
    },
    deletePhysician: async (physicianId) => {
        try {
            const response = await api.delete(`/emp-jp-doctors/${physicianId}`);
            return response.data;
        } catch (error) {
            console.error('Error deleting physician:', error);
            throw error;
        }
    },
    getDegreeLevels: async () => {
        try {
            const response = await api.get('/degree-levels/list');
            return response.data;
        } catch (error) {
            console.error('Error fetching degree levels:', error);
            throw error;
        }
    },
    getEducations: async (personId) => {
        try {
            const response = await api.get(`/hrEducations/${personId}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching educations:', error);
            throw error;
        }
    },
    addEducation: async (data) => {
        try {
            const payload = {
                personId: data.personId,
                degreeLevelId: data.degreeLevelId,
                hrEducationInstId: data.hrEducationInstId,
                specialty: data.specialty,
                docNo: data.docNo,
                docDate: data.docDate,
                graduationDate: data.graduationDate,
                note: data.note
            }
            const response = await api.post('/hrEducations', payload);
            return response.data;
        } catch (error) {
            console.error('Error adding education:', error);
            throw error;
        }
    },

    updateEducation: async (educationId, data) => {
        try {
            const payload = {
                personId: data.personId,
                degreeLevelId: data.degreeLevelId,
                hrEducationInstId: data.hrEducationInstId,
                specialty: data.specialty,
                docNo: data.docNo,
                docDate: data.docDate,
                graduationDate: data.graduationDate,
                note: data.note
            }
            const response = await api.put(`/hrEducations/${educationId}`, payload);
            return response.data;
        } catch (error) {
            console.error('Error updating education:', error);
            throw error;
        }
    },


    getHrEducationalInstitutions: async () => {
        try {
            const response = await api.get('/hrEducationInstList');
            return response.data;
        } catch (error) {
            console.error('Error fetching educational institutions:', error);
            throw error;
        }
    },

    getPersonFiles: async (personId) => {
        try {
            const response = await api.get(`/persons-file/${personId}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching files:', error);
            throw error;
        }
    },
    addPersonFile: async (personId, data) => {
        try {
            const formData = new FormData();
            formData.append('file', data.file);

            // Create the URL with encoded parameters
            const url = `/persons-file?` + new URLSearchParams({
                personId: personId,
                description: data.description || '',
                personsFileTypeId: data.personsFileTypeId,
                active: Boolean(data.active)
            }).toString();

            const response = await api.post(
                url,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );
            return response.data;
        } catch (error) {
            console.error('Error adding file:', error);
            throw error;
        }
    },
    updatePersonFile: async (personId, fileId, data) => {
        try {
            const payload = {
                personId: personId,
                description: data.description,
                active: Boolean(data.active)
            };
            const response = await api.put(`/persons-file/${fileId}`, payload);
            return response.data;
        } catch (error) {
            console.error('Error updating file:', error);
            throw error;
        }
    },
    deletePersonFile: async (fileId) => {
        try {
            const response = await api.delete(`/persons-file/${fileId}`);
            return response.data;
        } catch (error) {
            console.error('Error deleting file:', error);
            throw error;
        }
    },
    addEducation: async (data) => {
        try {
            const response = await api.post('/hrEducations', data);
            return response.data;
        } catch (error) {
            console.error('Error adding education:', error);
            throw error;
        }
    },
    updateEducation: async (educationId, data) => {
        try {
            const response = await api.put(`/hrEducations/${educationId}`, data);
            return response.data;
        } catch (error) {
            console.error('Error updating education:', error);
            throw error;
        }
    },
    deleteEducation: async (educationId) => {
        try {
            const response = await api.delete(`/hrEducations/${educationId}`);
            return response.data;
        } catch (error) {
            console.error('Error deleting education:', error);
            throw error;
        }
    },
    getFileTypes: async () => {
        try {
            const response = await api.get('/persons-file/type');
            return response.data;
        } catch (error) {
            console.error('Error fetching file types:', error);
            throw error;
        }
    },
    getLanguageList: async () => {
        try {
            const response = await api.get('/languageList');
            return response.data;
        } catch (error) {
            console.error('Error fetching language list:', error);
            throw error;
        }
    },
    getLangSkillsGrades: async () => {
        try {
            const response = await api.get('/langSkillsGrades');
            return response.data;
        } catch (error) {
            console.error('Error fetching language skill grades:', error);
            throw error;
        }
    },
    getPersonLanguages: async (personId) => {
        try {
            const response = await api.get(`/personalLanguageSkills/${personId}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching person languages:', error);
            throw error;
        }
    },
    addPersonLanguage: async (data) => {
        try {
            const payload = {
                personId: data.personId,
                language: data.language,
                writing: data.writing,
                listening: data.listening,
                spokenInteract: data.spokenInteract,
                spokenProduction: data.spokenProduction,
                reading: data.reading
            };
            const response = await api.post('/personalLanguageSkills', payload);
            return response.data;
        } catch (error) {
            console.error('Error adding person language:', error);
            throw error;
        }
    },
    updatePersonLanguage: async (personId, language, data) => {
        try {
            const payload = {
                personId: personId,
                language: data.language,
                writing: data.writing,
                listening: data.listening,
                spokenInteract: data.spokenInteract,
                spokenProduction: data.spokenProduction,
                reading: data.reading
            };
            const response = await api.put(`/personalLanguageSkills/${personId}/${language}`, payload);
            return response.data;
        } catch (error) {
            console.error('Error updating person language:', error);
            throw error;
        }
    },
    deletePersonLanguage: async (personId, language) => {
        try {
            const response = await api.delete(`/personalLanguageSkills/${personId}/${language}`);
            return response.data;
        } catch (error) {
            console.error('Error deleting person language:', error);
            throw error;
        }
    },
    getPersonFamily: async (id) => {
        try {
            const response = await api.get(`/emp-child/${id}/list`);
            return response.data;
        } catch (error) {
            console.error('Error fetching family members:', error);
            throw error;
        }
    },
    getFamilyMemberById: async (id) => {
        try {
            const response = await api.get(`/emp-child/${id}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching family member by id:', error);
            throw error;
        }
    },
    addFamilyMember: async (data) => {
        try {
            const payload = {
                govId: { idn: data.idn, identType: 0 },
                personId: data.personId,
                name: data.name,
                dateBorn: data.birthDate,
                school: Boolean(data.school),
                childNum: data.childOrder ? parseInt(data.childOrder, 10) : null,
                allowance: data.hasTaxRelief ? 1 : 0,
                isHeal: true,
                isBgCitizenship: data.citizenship === 'bulgarian'
            };
            const response = await api.post('/emp-child', payload);
            return response.data;
        } catch (error) {
            console.error('Error adding family member:', error);
            throw error;
        }
    },
    updateFamilyMember: async (id, data) => {
        try {
            const payload = {
                govId: { idn: data.idn, identType: 0 },
                personId: data.personId,
                name: data.name,
                dateBorn: data.birthDate,
                school: Boolean(data.school),
                childNum: data.childOrder ? parseInt(data.childOrder, 10) : null,
                allowance: data.hasTaxRelief ? 1 : 0,
                isHeal: true, // Default value as it's not in the form
                isBgCitizenship: data.citizenship === 'bulgarian'
            };
            const response = await api.put(`/emp-child/${id}`, payload);
            return response.data;
        } catch (error) {
            console.error('Error updating family member:', error);
            throw error;
        }
    },
    deleteFamilyMember: async (id) => {
        try {
            const response = await api.delete(`/emp-child/${id}`);
            return response.data;
        } catch (error) {
            console.error('Error deleting family member:', error);
            throw error;
        }
    },
    getJobHistory: async (personId) => {
        try {
            const response = await api.get(`/job-history/${personId}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching job history:', error);
            throw error;
        }
    },
    addJobHistory: async (data) => {
        try {
            const payload = {
                personId: data.personId,
                position: data.position,
                employerName: data.employer,
                fromDate: data.startDate,
                toDate: data.endDate,
                countryName: data.country
            };
            const response = await api.post('/job-history', payload);
            return response.data;
        } catch (error) {
            console.error('Error adding job history:', error);
            throw error;
        }
    },
    updateJobHistory: async (id, data) => {
        try {
            const payload = {
                personId: data.personId,
                position: data.position,
                employerName: data.employer,
                fromDate: data.startDate,
                toDate: data.endDate,
                countryName: data.country
            };
            const response = await api.put(`/job-history/${id}`, payload);
            return response.data;
        } catch (error) {
            console.error('Error updating job history:', error);
            throw error;
        }
    },
    deleteJobHistory: async (id) => {
        try {
            const response = await api.delete(`/job-history/${id}`);
            return response.data;
        } catch (error) {
            console.error('Error deleting job history:', error);
            throw error;
        }
    },
    getHomeOfficeAddress: async (personId) => {
        try {
            const response = await api.get(`/home-office-address/${personId}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching home office address:', error);
            throw error;
        }
    },
    getPersonPicture: async (id) => {
        try {
            const response = await api.get(`/persons/photo/${id}`, { responseType: 'blob' });
            return response;
        } catch {

            return null;
        }
    },
    updatePersonPhoto: async (personId, file) => {
        try {
            const formData = new FormData();
            formData.append('file', file);
            const response = await api.put(`/persons/photo/${personId}`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            return response.data;
        } catch (error) {
            console.error('Error updating person photo:', error);
            throw error;
        }
    },

    getWorkingCapacity: async (personId) => {
        try {
            const response = await api.get(`/disability/${personId}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching working capacity:', error);
            throw error;
        }
    },

    addWorkingCapacity: async (data) => {
        try {
            const payload = {
                personId: data.personId,
                fromDate: data.fromDate,         // Changed from startDate
                toDate: data.toDate,             // Changed from endDate
                percent: data.percent,           // Changed from percentage
                oldPercent: null,
                docId: parseInt(data.docId),
                orderNr: data.orderNr,          // Changed from orderNumber
                orderDate: data.orderDate,
                document: data.document
            };
            const response = await api.post('/disability', payload);
            return response.data;
        } catch (error) {
            console.error('Error adding working capacity:', error);
            throw error;
        }
    },

    updateWorkingCapacity: async (id, data) => {
        try {
            const payload = {
                personId: data.personId,
                fromDate: data.fromDate,
                toDate: data.toDate,
                percent: parseInt(data.percent),
                oldPercent: null,
                docId: parseInt(data.docId),
                orderNr: data.orderNr,
                orderDate: data.orderDate,
                document: data.document
            };
            const response = await api.put(`/disability/${id}`, payload);
            return response.data;
        } catch (error) {
            console.error('Error updating working capacity:', error);
            throw error;
        }
    },

    deleteWorkingCapacity: async (id) => {
        try {
            const response = await api.delete(`/disability/${id}`);
            return response.data;
        } catch (error) {
            console.error('Error deleting working capacity:', error);
            throw error;
        }
    },


    getSystemDocuments: async (personId) => {
        try {
            const response = await api.get(`/empDoc/${personId}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching system documents:', error);
            throw error;
        }
    },
    editSystemDocument: async (id, data) => {
        try {
            const payload = {
                personId: data.personId,
                docdate: data.docdate,
                empdoctype: data.empdoctype,
                specdate: data.specdate,
                description: data.description,
                note: data.note
            };
            const response = await api.put(`/empDoc/${id}`, payload);
            return response.data;
        } catch (error) {
            console.error('Error editing system document:', error);
            throw error;
        }
    },
    createSystemDocument: async (data) => {
        try {
            const payload = {
                personId: data.personId,
                docdate: data.docdate,
                empdoctype: data.empdoctype,
                specdate: data.specdate,
                description: data.description,
                note: data.note
            };
            const response = await api.post('/empDoc', payload);
            return response.data;
        } catch (error) {
            console.error('Error creating system document:', error);
            throw error;
        }
    },
    deleteSystemDocument: async (id) => {
        try {
            const response = await api.delete(`/empDoc/${id}`);
            return response.data;
        } catch (error) {
            console.error('Error deleting system document:', error);
            throw error;
        }
    },
    getPensionHistory: async (personId) => {
        try {
            const response = await api.get(`/amount-pension/${personId}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching pension history:', error);
            throw error;
        }
    },
    addPensionHistory: async (data) => {
        try {
            const payload = {
                personId: data.personId,
                fromDate: data.fromDate,
                toDate: "2200-10-10",
                amount: parseFloat(data.amount)
            };
            const response = await api.post('/amount-pension', payload);
            return response.data;
        } catch (error) {
            console.error('Error adding pension history:', error);
            throw error;
        }
    },
    updatePensionHistory: async (id, data) => {
        try {
            const payload = {
                personId: data.personId,
                fromDate: data.fromDate,
                toDate: "2200-10-10",
                amount: parseFloat(data.amount)
            };
            const response = await api.put(`/amount-pension/${id}`, payload);
            return response.data;
        } catch (error) {
            console.error('Error updating pension history:', error);
            throw error;
        }
    },
    deletePensionHistory: async (id) => {
        try {
            const response = await api.delete(`/amount-pension/${id}`);
            return response.data;
        } catch (error) {
            console.error('Error deleting pension history:', error);
            throw error;
        }
    }
};
